// One-time migration hook: moves localStorage data to server.
// Each module checks its localStorage key — if data exists, POSTs it to the server,
// then removes the key to prevent duplicate migration.

import { useEffect, useRef } from "react";
import { api } from "@/modules/equipment/lib/apiClient";

const MIGRATED_FLAG = "local-data-migrated-v1";

export function useMigrateLocalData() {
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Skip if already migrated
    if (localStorage.getItem(MIGRATED_FLAG)) return;

    (async () => {
      try {
        await migrateTmc();
        await migrateVisits();
        await migrateAudit5sRecords();
        await migrateAudit5sTemplates();
        localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());
        console.log("[migrate-local] Migration complete");
      } catch (e) {
        console.error("[migrate-local] Migration error:", e);
      }
    })();
  }, []);
}

// ── TMC items ───────────────────────────────────────────────────────
async function migrateTmc() {
  const key = "tmc-items-v4";
  const raw = localStorage.getItem(key);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw);
    const items: any[] = parsed?.state?.items || parsed?.items || [];
    if (items.length === 0) return;

    // Check if server is empty
    const existing = await api<{ data: any[] }>("/v1/tmc");
    if (existing.data.length > 0) {
      localStorage.removeItem(key);
      return;
    }

    for (const item of items) {
      await api("/v1/tmc", {
        method: "POST",
        body: {
          name: item.name,
          unit: item.unit,
          tag: item.tag || undefined,
          current_stock: item.currentStock ?? 0,
          avg_daily: item.avgDaily ?? 0,
        },
      });
    }
    localStorage.removeItem(key);
    console.log(`[migrate-local] TMC: migrated ${items.length} items`);
  } catch (e) {
    console.error("[migrate-local] TMC migration error:", e);
  }
}

// ── Visits (shifts + visits) ────────────────────────────────────────
async function migrateVisits() {
  const key = "visits-v1";
  const raw = localStorage.getItem(key);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw);
    const data = parsed?.state || parsed;
    const shifts: any[] = data?.shifts || [];
    const visits: any[] = data?.visits || [];

    if (shifts.length === 0 && visits.length === 0) return;

    // Check if server already has shifts
    const existing = await api<{ data: any[] }>("/v1/visits/shifts");
    if (existing.data.length > 0) {
      localStorage.removeItem(key);
      return;
    }

    // Migrate shifts + responsibles
    const idMap: Record<string, number> = {}; // old local id → server id
    for (const shift of shifts) {
      const res = await api<{ data: { id: number } }>("/v1/visits/shifts", {
        method: "POST",
        body: { name: shift.name },
      });
      const serverId = res.data.id;
      idMap[shift.id] = serverId;

      for (const r of (shift.responsibles || [])) {
        await api(`/v1/visits/shifts/${serverId}/responsibles`, {
          method: "POST",
          body: { name: r.name, phone: r.phone || "" },
        });
      }
    }

    // Migrate visits
    for (const v of visits) {
      const shiftServerId = idMap[v.shiftId];
      if (!shiftServerId) continue;
      await api("/v1/visits", {
        method: "POST",
        body: {
          shift_id: shiftServerId,
          fio: v.fio,
          org: v.org || "",
          purpose: v.purpose || "",
          date: v.date,
          escort: !!v.escort,
        },
      });
    }

    localStorage.removeItem(key);
    console.log(`[migrate-local] Visits: migrated ${shifts.length} shifts, ${visits.length} visits`);
  } catch (e) {
    console.error("[migrate-local] Visits migration error:", e);
  }
}

// ── Audit5S records (order/gates/fire/damage log) ───────────────────
async function migrateAudit5sRecords() {
  const key = "audit5s-log-v3";
  const raw = localStorage.getItem(key);
  if (!raw) return;

  try {
    const records: any[] = JSON.parse(raw);
    if (records.length === 0) return;

    // Check if server already has records
    const existing = await api<{ data: any[] }>("/v1/audit5s?limit=1");
    if (existing.data.length > 0) {
      localStorage.removeItem(key);
      return;
    }

    for (const rec of records) {
      // Strip photos from results (they were object URLs, not recoverable)
      const cleanResults: Record<string, { status: string | null; note: string }> = {};
      for (const [k, v] of Object.entries(rec.results || {})) {
        const item = v as any;
        cleanResults[k] = { status: item.status, note: item.note || "" };
      }

      await api("/v1/audit5s", {
        method: "POST",
        body: {
          type: rec.type,
          shift: rec.shift,
          recorded_at: new Date(rec.when).toISOString(),
          results: cleanResults,
          summary: rec.summary,
        },
      });
    }

    localStorage.removeItem(key);
    console.log(`[migrate-local] Audit5S records: migrated ${records.length} records`);
  } catch (e) {
    console.error("[migrate-local] Audit5S records migration error:", e);
  }
}

// ── Audit5S templates + inspections ─────────────────────────────────
async function migrateAudit5sTemplates() {
  const key = "five-s-journal-v1";
  const raw = localStorage.getItem(key);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw);
    const data = parsed?.state || parsed;
    const templates: Record<string, any> = data?.templates || {};
    const inspections: Record<string, any> = data?.inspections || {};

    const tplList = Object.values(templates);
    const inspList = Object.values(inspections);
    if (tplList.length === 0 && inspList.length === 0) return;

    // Check if server already has templates
    const existing = await api<{ data: any[] }>("/v1/audit5s/templates");
    if (existing.data.length > 0) {
      localStorage.removeItem(key);
      return;
    }

    // Migrate templates
    const tplIdMap: Record<string, number> = {};
    for (const tpl of tplList) {
      const res = await api<{ data: { id: number } }>("/v1/audit5s/templates", {
        method: "POST",
        body: {
          title: tpl.title,
          frequency: tpl.frequency,
          items: tpl.items,
          archived: !!tpl.archived,
        },
      });
      tplIdMap[tpl.id] = res.data.id;
    }

    // Migrate inspections
    for (const insp of inspList) {
      const servTplId = tplIdMap[insp.templateId];
      if (!servTplId) continue;
      await api("/v1/audit5s/inspections", {
        method: "POST",
        body: {
          template_id: servTplId,
          template_title: insp.templateTitle,
          date: new Date(insp.date).toISOString(),
          area: insp.area || "",
          inspector: insp.inspector || "",
          results: insp.results,
        },
      });
    }

    localStorage.removeItem(key);
    console.log(`[migrate-local] Audit5S templates: migrated ${tplList.length} templates, ${inspList.length} inspections`);
  } catch (e) {
    console.error("[migrate-local] Audit5S templates migration error:", e);
  }
}
